import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Plus, MapPin, Clock, UserCog, FileDown, PenLine, CheckCircle2, ChevronsUpDown, Search } from 'lucide-react';
import SignatureDialog from '@/components/SignatureDialog';
import VoiceRecognition from '@/components/VoiceRecognition';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  {
    key: 'section1_presentation',
    title: "1. Présentation & tenue de l'agent",
    items: ['Présence au poste à l\'heure', 'Tenue réglementaire complète', 'Carte pro', 'Attitude professionnelle']
  },
  {
    key: 'section2_connaissance',
    title: '2. Connaissance du site et des consignes',
    hasScenario: true,
    scenarioKey: 'scenario_section2',
    items: ['Connaissance des consignes du poste', 'Connaissance du site', "Procédures d'urgence"]
  },
  {
    key: 'section3_equipement',
    title: '3. Équipement et matériel',
    items: [
      { label: 'Équipements en bon état', hint: 'Radios – PTI – Lampes – Main courante' },
      'Utilisation correcte',
      'Main courante tenue à jour'
    ]
  },
  {
    key: 'section4_rondes',
    title: '4. Rondes et surveillance',
    hasScenario: true,
    scenarioKey: 'scenario_section4',
    items: ['Rondes réalisées selon consignes', 'Contrôles des points sensibles', 'Signalement des anomalies']
  },
  {
    key: 'section5_relationnel',
    title: '5. Relationnel et communication',
    items: ['Accueil des usagers / visiteurs', 'Communication hiérarchique', 'Rédaction des rapports']
  }
];

const EVALUATIONS = ['Très satisfaisant', 'Satisfaisant', 'À améliorer', 'Insuffisant'];

const getParisDate = () => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(now).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const emptySections = () => {
  const obj = {};
  SECTIONS.forEach(s => {
    obj[s.key] = s.items.map(it => ({
      label: typeof it === 'string' ? it : it.label,
      status: null,
      observations: ''
    }));
    if (s.hasScenario) obj[s.scenarioKey] = '';
  });
  return obj;
};

const emptyForm = () => ({
  control_date: getParisDate(),
  time_start: '',
  time_end: '',
  agent_name: '',
  agent_position: '',
  site_id: '',
  // Saisie manuelle (admin uniquement) : pour contrôler un agent sur un site
  // qui n'a pas la main courante électronique, donc absent de la liste des
  // sites - site_name est alors renseigné à la main plutôt que site_id.
  manual_entry: false,
  site_name: '',
  general_comments: '',
  evaluation: '',
  controller_signature: null,
  agent_signature: null,
  ...emptySections()
});

const AgentControlPanel = ({ sites, user }) => {
  const isAdmin = user?.role === 'admin';
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  const [signingWho, setSigningWho] = useState(null); // 'controller' | 'agent' | null
  const lastSignatureCloseRef = useRef(0);
  const [siteAgents, setSiteAgents] = useState([]);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [allAgents, setAllAgents] = useState([]);
  const [searchAgentPickerOpen, setSearchAgentPickerOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Recherche de fiches
  const [searchAgentName, setSearchAgentName] = useState('');
  const [searchControllerName, setSearchControllerName] = useState('');
  const [searchSiteId, setSearchSiteId] = useState('all');
  const [isSearching, setIsSearching] = useState(false);

  const token = localStorage.getItem('token');

  // Les fiches ne se chargent plus automatiquement : elles sont désormais
  // uniquement consultables via la recherche, pour ne pas encombrer l'écran.

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/agents-accessible`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setAllAgents(await res.json());
      } catch { /* silencieux */ }
    })();
  }, []);

  // Un non-admin n'a pas la possibilité de choisir "Tous les sites" : son
  // site (unique, ou celui choisi à la connexion) est sélectionné directement.
  useEffect(() => {
    if (user?.role !== 'admin' && sites.length > 0) {
      setSearchSiteId(prev => (prev === 'all' ? sites[0].id : prev));
    }
    // eslint-disable-next-line
  }, [sites, user]);

  // Recharge la liste des agents affectés au site dès qu'un site est sélectionné
  useEffect(() => {
    if (!formData.site_id) { setSiteAgents([]); return; }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sites/${formData.site_id}/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setSiteAgents(await res.json());
      } catch { /* silencieux */ }
    })();
  }, [formData.site_id]);

  const loadReports = async (overrides = {}) => {
    const siteId = overrides.siteId !== undefined ? overrides.siteId : searchSiteId;
    const agentNameVal = overrides.agentName !== undefined ? overrides.agentName : searchAgentName;
    const controllerNameVal = overrides.controllerName !== undefined ? overrides.controllerName : searchControllerName;
    try {
      const params = new URLSearchParams();
      if (siteId !== 'all') params.append('site_id', siteId);
      if (agentNameVal.trim()) params.append('agent_name', agentNameVal.trim());
      if (controllerNameVal.trim()) params.append('controller_name', controllerNameVal.trim());

      const res = await fetch(`${BACKEND_URL}/api/agent-control-reports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const activeSearch = siteId !== 'all' || agentNameVal.trim() || controllerNameVal.trim();
        setIsSearching(!!activeSearch);
        setReports(activeSearch ? data : data.slice(0, 3));
      }
    } catch { /* silencieux, non bloquant pour le tableau de bord */ }
    finally { setLoading(false); }
  };

  const handleSearch = () => {
    loadReports();
  };

  const resetSearch = () => {
    setSearchAgentName('');
    setSearchControllerName('');
    setSearchSiteId('all');
    loadReports({ siteId: 'all', agentName: '', controllerName: '' });
  };

  const updateItem = (sectionKey, idx, field, value) => {
    setFormData(prev => {
      const items = [...prev[sectionKey]];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, [sectionKey]: items };
    });
  };

  // Ajoute le morceau dicté au texte déjà présent, sans jamais l'écraser
  // (même logique que la dictée de la main courante et des consignes)
  const appendObservation = (sectionKey, idx, chunk) => {
    setFormData(prev => {
      const items = [...prev[sectionKey]];
      items[idx] = { ...items[idx], observations: (items[idx].observations || '') + chunk };
      return { ...prev, [sectionKey]: items };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.manual_entry) {
      if (!formData.site_name.trim() || !formData.agent_name.trim() || !formData.control_date) {
        toast.error('Nom du site, nom de l\'agent et date du contrôle sont requis');
        return;
      }
    } else if (!formData.site_id || !formData.agent_name.trim() || !formData.control_date) {
      toast.error('Site, nom de l\'agent et date du contrôle sont requis');
      return;
    }
    if (!formData.scenario_section2.trim() || !formData.scenario_section4.trim()) {
      toast.error('Le scénario est obligatoire pour les sections 2 et 4');
      return;
    }
    if (!formData.time_start || !formData.time_end) {
      toast.error('L\'heure de début et l\'heure de fin sont requises');
      return;
    }
    setSubmitting(true);
    try {
      const { time_start, time_end, ...rest } = formData;
      const payload = { ...rest, time_range: `${time_start} - ${time_end}` };
      const res = await fetch(`${BACKEND_URL}/api/agent-control-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success('Fiche de contrôle enregistrée et PDF généré');
        setShowForm(false);
        setFormData(emptyForm());
        loadReports();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const getSiteName = (id) => sites.find(s => s.id === id)?.name || 'Site inconnu';
  // Pour une fiche en saisie manuelle (site_id absent), le nom du site n'existe
  // que sous forme de texte libre (site_name), déjà renvoyé par le serveur.
  const displaySiteName = (r) => (r.site_id ? getSiteName(r.site_id) : (r.site_name || 'Site inconnu'));

  const downloadPdf = async (reportId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/agent-control-reports/${reportId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        toast.error('Impossible de télécharger le PDF');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2 mb-4" style={{ fontFamily: 'Space Grotesk' }}>
        <UserCog className="h-6 w-6 text-slate-500 dark:text-slate-400" />
        Contrôle des agents
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Dialog
          open={showForm}
          onOpenChange={(open) => {
            // Empêche la fenêtre principale de se fermer par erreur juste après
            // la fermeture de la fenêtre de signature (bug connu de fenêtres
            // imbriquées où la fermeture de l'une déclenche par erreur celle de l'autre)
            if (!open && Date.now() - lastSignatureCloseRef.current < 400) return;
            setShowForm(open);
          }}
        >
          <DialogTrigger asChild>
            <Card className="glass-effect card-hover cursor-pointer" data-testid="new-agent-control-btn">
              <CardContent className="pt-6 flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center shrink-0">
                  <Plus className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-100">Nouvelle fiche de contrôle</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Remplir une fiche réglementaire pour un agent</p>
                </div>
              </CardContent>
            </Card>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Fiche de contrôle agent — réglementaire</DialogTitle>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Conformément à l'article R631-16 du Code de la sécurité intérieure.
              </p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Informations générales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
                <div>
                  <Label className="mb-1 block">Date du contrôle *</Label>
                  <Input type="date" value={formData.control_date} onChange={(e) => setFormData(prev => ({ ...prev, control_date: e.target.value }))} />
                </div>
                <div>
                  <Label className="mb-1 block">Heure de début *</Label>
                  <Input type="time" value={formData.time_start} onChange={(e) => setFormData(prev => ({ ...prev, time_start: e.target.value }))} />
                </div>
                {isAdmin && (
                  <div className="md:col-span-2 flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900">
                    <Checkbox
                      id="manual-entry-checkbox"
                      checked={formData.manual_entry}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev, manual_entry: !!checked, site_id: '', site_name: '', agent_name: ''
                      }))}
                      data-testid="manual-entry-checkbox"
                    />
                    <label htmlFor="manual-entry-checkbox" className="text-sm text-amber-800 dark:text-amber-300 cursor-pointer">
                      Site sans main courante électronique (saisie manuelle du site et de l'agent)
                    </label>
                  </div>
                )}

                {formData.manual_entry ? (
                  <>
                    <div>
                      <Label className="mb-1 block">Nom du site *</Label>
                      <Input
                        placeholder="Nom du site contrôlé"
                        value={formData.site_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, site_name: e.target.value }))}
                        data-testid="manual-site-name-input"
                      />
                    </div>
                    <div>
                      <Label className="mb-1 block">Nom de l'agent *</Label>
                      <Input
                        placeholder="Nom de l'agent contrôlé"
                        value={formData.agent_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, agent_name: e.target.value }))}
                        data-testid="manual-agent-name-input"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="md:col-span-2">
                      <Label className="mb-1 block">Site contrôlé *</Label>
                      <Select
                        value={formData.site_id}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v, agent_name: '' }))}
                      >
                        <SelectTrigger><SelectValue placeholder="Sélectionnez un site" /></SelectTrigger>
                        <SelectContent>
                          {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-1 block">Nom de l'agent *</Label>
                      <Popover open={agentPickerOpen} onOpenChange={setAgentPickerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            disabled={!formData.site_id}
                            className="w-full justify-between font-normal"
                            data-testid="agent-picker-trigger"
                          >
                            {formData.agent_name || (formData.site_id ? 'Sélectionnez un agent' : 'Choisissez d\'abord un site')}
                            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0">
                          <Command>
                            <CommandInput placeholder="Rechercher un agent..." />
                            <CommandList>
                              <CommandEmpty>Aucun agent trouvé pour ce site.</CommandEmpty>
                              <CommandGroup>
                                {siteAgents.map(a => (
                                  <CommandItem
                                    key={a.id}
                                    value={a.name}
                                    onSelect={() => {
                                      setFormData(prev => ({ ...prev, agent_name: a.name }));
                                      setAgentPickerOpen(false);
                                    }}
                                  >
                                    {a.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </>
                )}
                <div>
                  <Label className="mb-1 block">Poste de l'agent</Label>
                  <Input value={formData.agent_position} onChange={(e) => setFormData(prev => ({ ...prev, agent_position: e.target.value }))} />
                </div>
              </div>

              {/* Sections de contrôle */}
              {SECTIONS.map(section => (
                <div key={section.key} className="space-y-3">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">{section.title}</h3>
                  {section.hasScenario && (
                    <div>
                      <Label className="mb-1 block">Scénario *</Label>
                      <Input
                        placeholder="Décrivez le scénario du contrôle"
                        value={formData[section.scenarioKey]}
                        onChange={(e) => setFormData(prev => ({ ...prev, [section.scenarioKey]: e.target.value }))}
                        required
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    {formData[section.key].map((item, idx) => {
                      const hint = typeof section.items[idx] === 'object' ? section.items[idx].hint : null;
                      return (
                        <div key={idx} className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{item.label}</p>
                              {hint && <p className="text-xs text-gray-400 dark:text-slate-500">{hint}</p>}
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <button
                                type="button"
                                onClick={() => updateItem(section.key, idx, 'status', 'conforme')}
                                className={`px-3 py-1 rounded-md text-xs font-medium border ${item.status === 'conforme' ? 'bg-green-500 text-white border-green-500' : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'}`}
                              >
                                Conforme
                              </button>
                              <button
                                type="button"
                                onClick={() => updateItem(section.key, idx, 'status', 'non_conforme')}
                                className={`px-3 py-1 rounded-md text-xs font-medium border ${item.status === 'non_conforme' ? 'bg-red-500 text-white border-red-500' : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'}`}
                              >
                                Non conforme
                              </button>
                            </div>
                          </div>
                          <Input
                            className="mt-2"
                            placeholder="Observations..."
                            value={item.observations}
                            onChange={(e) => updateItem(section.key, idx, 'observations', e.target.value)}
                          />
                          <VoiceRecognition
                            onTranscript={(chunk) => appendObservation(section.key, idx, chunk)}
                            fieldName={`observations-${section.key}-${idx}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Heure de fin - renseignée à la fin du contrôle, mais affichée en haut du PDF avec l'heure de début */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
                <Label className="mb-1 block">Heure de fin *</Label>
                <Input type="time" value={formData.time_end} onChange={(e) => setFormData(prev => ({ ...prev, time_end: e.target.value }))} />
              </div>

              {/* Appréciation générale */}
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-800 dark:text-slate-100">Appréciation générale</h3>
                <div>
                  <Label className="mb-1 block">Commentaires du contrôleur</Label>
                  <Textarea rows={3} value={formData.general_comments} onChange={(e) => setFormData(prev => ({ ...prev, general_comments: e.target.value }))} />
                </div>
                <div>
                  <Label className="mb-1 block">Évaluation</Label>
                  <Select value={formData.evaluation} onValueChange={(v) => setFormData(prev => ({ ...prev, evaluation: v }))}>
                    <SelectTrigger><SelectValue placeholder="Sélectionnez une évaluation" /></SelectTrigger>
                    <SelectContent>
                      {EVALUATIONS.map(ev => <SelectItem key={ev} value={ev}>{ev}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Signatures */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1 block">Signature du contrôleur</Label>
                  {formData.controller_signature ? (
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                      <CheckCircle2 className="h-4 w-4" /> Signé
                      <Button type="button" variant="ghost" size="sm" onClick={() => setSigningWho('controller')}>Modifier</Button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => setSigningWho('controller')}>
                      <PenLine className="h-4 w-4 mr-2" /> Signer
                    </Button>
                  )}
                </div>
                <div>
                  <Label className="mb-1 block">Signature de l'agent</Label>
                  {formData.agent_signature ? (
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                      <CheckCircle2 className="h-4 w-4" /> Signé
                      <Button type="button" variant="ghost" size="sm" onClick={() => setSigningWho('agent')}>Modifier</Button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => setSigningWho('agent')}>
                      <PenLine className="h-4 w-4 mr-2" /> Signer
                    </Button>
                  )}
                </div>
              </div>

              <Button type="submit" disabled={submitting} className="w-full btn-primary">
                {submitting ? 'Génération du PDF...' : 'Enregistrer et générer le PDF'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showSearch} onOpenChange={setShowSearch}>
          <DialogTrigger asChild>
            <Card className="glass-effect card-hover cursor-pointer" data-testid="search-agent-control-btn">
              <CardContent className="pt-6 flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0">
                  <Search className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-100">Rechercher une fiche</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Retrouver un contrôle par agent, site ou contrôleur</p>
                </div>
              </CardContent>
            </Card>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Rechercher une fiche de contrôle</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col md:flex-row gap-2 mb-3">
              <Popover open={searchAgentPickerOpen} onOpenChange={setSearchAgentPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    className="flex-1 justify-between font-normal"
                    data-testid="search-agent-picker-trigger"
                  >
                    {searchAgentName || 'Rechercher par agent contrôlé...'}
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0">
                  <Command>
                    <CommandInput placeholder="Rechercher un agent..." />
                    <CommandList>
                      <CommandEmpty>Aucun agent trouvé.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__tous__"
                          onSelect={() => { setSearchAgentName(''); setSearchAgentPickerOpen(false); }}
                        >
                          Tous les agents
                        </CommandItem>
                        {allAgents.map(a => (
                          <CommandItem
                            key={a.id}
                            value={a.name}
                            onSelect={() => { setSearchAgentName(a.name); setSearchAgentPickerOpen(false); }}
                          >
                            {a.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Select value={searchSiteId} onValueChange={setSearchSiteId}>
                <SelectTrigger className="md:w-48"><SelectValue placeholder="Tous les sites" /></SelectTrigger>
                <SelectContent>
                  {user?.role === 'admin' && <SelectItem value="all">Tous les sites</SelectItem>}
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 mb-4">
              <Button type="button" className="btn-primary flex-1" onClick={handleSearch} data-testid="search-reports-btn">
                <Search className="h-4 w-4 mr-2" /> Rechercher
              </Button>
              {isSearching && (
                <Button type="button" variant="ghost" onClick={resetSearch}>Réinitialiser</Button>
              )}
            </div>

            {loading ? (
              <p className="text-sm text-gray-500 dark:text-slate-400">Chargement...</p>
            ) : reports.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-4">
                {isSearching ? 'Aucune fiche ne correspond à cette recherche' : 'Lancez une recherche pour retrouver une fiche de contrôle'}
              </p>
            ) : (
              <div className="space-y-3">
                {reports.map(r => (
                  <div key={r.id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{r.agent_name}</span>
                        {r.evaluation && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-md">{r.evaluation}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {displaySiteName(r)}
                        <Clock className="h-3 w-3 ml-2" /> {r.control_date}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-slate-500">Contrôle réalisé par {r.controller_name}</p>
                    </div>
                    {r.pdf_file_id && (
                      <Button variant="outline" size="sm" onClick={() => downloadPdf(r.id)}>
                        <FileDown className="h-4 w-4 mr-1" /> PDF
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <SignatureDialog
        open={signingWho === 'controller'}
        onClose={() => { setSigningWho(null); lastSignatureCloseRef.current = Date.now(); }}
        onSign={(sig) => { setFormData(prev => ({ ...prev, controller_signature: sig })); setSigningWho(null); lastSignatureCloseRef.current = Date.now(); }}
        title="Signature du contrôleur"
      />
      <SignatureDialog
        open={signingWho === 'agent'}
        onClose={() => { setSigningWho(null); lastSignatureCloseRef.current = Date.now(); }}
        onSign={(sig) => { setFormData(prev => ({ ...prev, agent_signature: sig })); setSigningWho(null); lastSignatureCloseRef.current = Date.now(); }}
        title="Signature de l'agent"
      />
    </div>
  );
};

export default AgentControlPanel;
